export type AchAccountSource = 'plaid' | 'moneythumb';

export type AchFlatAccount = {
  source: AchAccountSource;
  institutionName: string;
  connectionStatus: string;
  id: string;
  accountName: string;
  mask: string;
  accountType: string;
  achStatus: string;
  verificationMethod?: string;
  product?: string;
  consentGiven?: boolean;
  bankDetailId?: string | number;
  plaidItemId?: string | number;
};

export type AchBankRow = {
  id: string;
  source: AchAccountSource;
  institutionName: string;
  achStatus: string;
  connectionStatus?: string;
  accounts: AchFlatAccount[];
};

export const isCheckingAccount = (accountType?: string) => {
  const normalized = String(accountType || '')
    .trim()
    .toLowerCase();
  return normalized.includes('checking');
};

/** Compare last 4 digits so "7609" matches "0007609" / "****7609". */
export const normalizeMask = (mask?: string) => {
  const digits = String(mask || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.slice(-4);
};

export const isAchVerifiedStatus = (status?: string) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();
  return [
    'verified',
    'complete',
    'automatically_verified',
    'manually_verified',
  ].includes(normalized);
};

export const formatAchStatus = (status?: string) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();
  if (!normalized || normalized === 'not_started' || normalized === '-')
    return 'Not Started';
  if (normalized === 'verified' || normalized === 'complete')
    return 'Connected';
  if (normalized === 'pending') return 'Verification Pending';
  return normalized
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/** Group by institution only so Plaid + MoneyThumb for the same bank share a row. */
export const getBankGroupKey = (institutionName: string) =>
  String(institutionName || '')
    .trim()
    .toLowerCase();

/** Merge key: same last-4 mask within an institution (source-agnostic). */
export const getAccountMergeKey = (mask?: string) => normalizeMask(mask);

export const buildBankRows = (flatAccounts: AchFlatAccount[]): AchBankRow[] => {
  const banksMap: Record<string, AchBankRow> = {};

  flatAccounts.forEach((acc) => {
    const key = getBankGroupKey(acc.institutionName);
    if (!banksMap[key]) {
      banksMap[key] = {
        id: key,
        source: acc.source,
        institutionName: acc.institutionName,
        connectionStatus: acc.connectionStatus,
        achStatus: acc.achStatus,
        accounts: [],
      };
    }
    const bank = banksMap[key];
    bank.accounts.push(acc);
    // Prefer plaid when sources are mixed (ACH transfer/consent is plaid-based).
    if (acc.source === 'plaid') {
      bank.source = 'plaid';
    }
    if (acc.connectionStatus === 'connected') {
      bank.connectionStatus = 'connected';
    }
    if (isAchVerifiedStatus(acc.achStatus)) {
      bank.achStatus = 'verified';
    }
  });

  return Object.values(banksMap);
};

export const getAchSelectableAccounts = (banks: AchBankRow[]) => {
  // Accounts already covered by a Plaid transfer (same institution + mask).
  const transferCoveredKeys = new Set(
    banks.flatMap((bank) =>
      bank.accounts
        .filter((acc) => acc.source === 'plaid' && acc.product === 'transfer')
        .map(
          (acc) =>
            `${getBankGroupKey(bank.institutionName)}::${normalizeMask(
              acc.mask
            )}`
        )
    )
  );

  const list: (AchFlatAccount & { institutionName: string })[] = [];

  banks.forEach((bank) => {
    bank.accounts.forEach((acc) => {
      if (acc.source === 'plaid' && acc.product === 'transfer') return;
      if (!isCheckingAccount(acc.accountType)) return;
      const coveredKey = `${getBankGroupKey(
        bank.institutionName
      )}::${normalizeMask(acc.mask)}`;
      // Skip MT/transactions duplicates already authorized via transfer.
      if (transferCoveredKeys.has(coveredKey)) return;
      list.push({ ...acc, institutionName: bank.institutionName });
    });
  });

  return list;
};

export const hasPlaidTransferProduct = (banks: AchBankRow[]) =>
  banks.some((bank) =>
    bank.accounts.some(
      (acc) => acc.source === 'plaid' && acc.product === 'transfer'
    )
  );

export const hasPendingAchConsent = (banks: AchBankRow[]) =>
  banks.some((bank) =>
    bank.accounts.some(
      (acc) =>
        acc.source === 'plaid' &&
        acc.product === 'transfer' &&
        isAchVerifiedStatus(acc.achStatus) &&
        acc.consentGiven === false
    )
  );

export const hasPendingMicrodeposit = (banks: AchBankRow[]) =>
  banks.some((bank) =>
    bank.accounts.some(
      (acc) =>
        acc.achStatus === 'pending' &&
        (acc.verificationMethod === 'manually_deposit' ||
          acc.verificationMethod === 'manual_microdeposit')
    )
  );

export const shouldShowActionColumn = (banks: AchBankRow[]) => {
  const selectable = getAchSelectableAccounts(banks);
  return (
    selectable.length > 0 ||
    hasPendingAchConsent(banks) ||
    hasPendingMicrodeposit(banks)
  );
};

/** Non-checking Plaid transactions or MoneyThumb accounts (used to open ACH modal info). */
export const hasNonCheckingTransactionOrMtAccount = (banks: AchBankRow[]) =>
  banks.some((bank) =>
    bank.accounts.some((acc) => {
      const isMtOrTransactions =
        acc.source === 'moneythumb' ||
        (acc.source === 'plaid' && acc.product === 'transactions');
      return isMtOrTransactions && !isCheckingAccount(acc.accountType);
    })
  );

export type PrimaryCtaState = {
  label: 'Begin ACH Authorization' | 'Submit' | 'Next Step';
  disabled: boolean;
  action: 'open_auth_modal' | 'submit';
};

export const getPrimaryCtaState = (
  banks: AchBankRow[],
  hasTruepicInspection: boolean
): PrimaryCtaState => {
  const hasTransfer = hasPlaidTransferProduct(banks);
  const selectableCount = getAchSelectableAccounts(banks).length;
  const pendingConsent = hasPendingAchConsent(banks);

  if (hasTransfer) {
    return {
      label: hasTruepicInspection ? 'Next Step' : 'Submit',
      disabled: pendingConsent,
      action: 'submit',
    };
  }

  return {
    label: 'Begin ACH Authorization',
    disabled:
      selectableCount === 0 && !hasNonCheckingTransactionOrMtAccount(banks),
    action: 'open_auth_modal',
  };
};

export const masksMatchForAch = (
  selectedMask?: string,
  linkedMask?: string
) => {
  const selected = normalizeMask(selectedMask);
  const linked = normalizeMask(linkedMask);
  // Both masks required — empty must not pass as a match.
  if (!selected || !linked) return false;
  return selected === linked;
};
